import type { DiagramDocument, DiagramPage } from './types';

export interface PageLinkImpact {
  pageId: string;
  pageName: string;
  nodeId: string;
  nodeText: string;
}

export interface PageDeletionImpact {
  pageId: string;
  pageName: string;
  nodeCount: number;
  edgeCount: number;
  incomingLinks: PageLinkImpact[];
  isActive: boolean;
  fallbackPageId: string | null;
}

export function analyzePageDeletion(document: DiagramDocument, pageId: string): PageDeletionImpact | null {
  const index = document.pages.findIndex((page) => page.id === pageId);
  if (index < 0) return null;
  const page = document.pages[index];
  const remaining = document.pages.filter((item) => item.id !== pageId);
  const fallback = remaining[Math.max(0, Math.min(index - 1, remaining.length - 1))] ?? remaining[0] ?? null;
  const incomingLinks: PageLinkImpact[] = [];
  for (const sourcePage of document.pages) {
    if (sourcePage.id === pageId) continue;
    for (const node of sourcePage.nodes) {
      if (node.data?.linkPageId === pageId) incomingLinks.push({ pageId: sourcePage.id, pageName: sourcePage.name, nodeId: node.id, nodeText: node.text });
    }
  }
  return {
    pageId,
    pageName: page.name,
    nodeCount: page.nodes.length,
    edgeCount: page.edges.length,
    incomingLinks,
    isActive: document.activePageId === pageId,
    fallbackPageId: fallback?.id ?? null,
  };
}

export function removePageWithLinkCleanup(document: DiagramDocument, pageId: string, fallbackPageId?: string | null): DiagramDocument {
  if (document.pages.length <= 1) return document;
  const impact = analyzePageDeletion(document, pageId);
  if (!impact) return document;
  const pages = document.pages
    .filter((page) => page.id !== pageId)
    .map((page): DiagramPage => ({
      ...page,
      nodes: page.nodes.map((node) => node.data?.linkPageId === pageId ? { ...node, data: { ...node.data, linkPageId: null } } : node),
    }));
  const requestedFallback = fallbackPageId && pages.some((page) => page.id === fallbackPageId) ? fallbackPageId : null;
  const activePageId = document.activePageId === pageId
    ? requestedFallback ?? impact.fallbackPageId ?? pages[0].id
    : document.activePageId;
  return { ...document, pages, activePageId, updatedAt: new Date().toISOString() };
}
